import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CompanyResponse } from '@shared/schemas';

import { initialState } from './companies.state';

/** Payload for `loadSucceeded`: the fetched page + grand total. */
export interface LoadedPage {
  rows: CompanyResponse[];
  total: number;
}

/**
 * Admin Companies list slice. Reducers are pure state transitions only; the
 * paginated fetch lives in the saga, which reads `page` / `pageSize` back from
 * this state and dispatches these `load*` transitions as it progresses. `pageSet`
 * / `pageSizeSet` only move the cursor; the saga triggers the refetch.
 */
const slice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    // ── Cursor (the saga refetches after applying these) ──
    pageSet: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    /** Changing page size resets to the first page (mirrors the old useInfiniteApi). */
    pageSizeSet: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.page = 0;
    },

    // ── Load lifecycle ──
    loadStarted: (state) => {
      state.loading = true;
      state.error = null;
    },
    loadSucceeded: (state, action: PayloadAction<LoadedPage>) => {
      state.rows = action.payload.rows;
      state.total = action.payload.total;
      state.loading = false;
    },
    loadFailed: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const companiesActions = slice.actions;
export const companiesReducer = slice.reducer;
